import { ClerkProvider } from '@clerk/react-router'
import { ErrorBoundary } from '@datadog/browser-rum-react'
import { HelmetProvider } from '@dr.pogodin/react-helmet'
import type { QueryClient } from '@tanstack/react-query'
import { QueryClientProvider } from '@tanstack/react-query'
import { domMax, LazyMotion } from 'motion/react'
import type { ReactNode } from 'react'

import {
  Button,
  ErrorState,
  NoOpRedactionProvider,
  ThemeProvider,
  Toaster,
  TooltipProvider,
} from '../../core'
import { JoyfulAppProvider } from '../utils/appContext'
import type { JoyfulAppRuntime } from '../utils/appContext'
import { reportBrowserError } from '../utils/browserErrorReporter'
import { shouldSuppressStaleClientBundleError } from '../utils/vitePreloadErrorRecovery'
import {
  AgentTabNotification,
  GlobalAgentStreamingProvider,
} from './GlobalAgentStreamingContext'
import { DocumentTitleProvider } from './PageTitle'

export interface JoyfulWebAppProviderProps {
  app: JoyfulAppRuntime
  children: ReactNode
  clerkPublishableKey: string
  queryClient?: QueryClient
  renderQueryProvider?: (children: ReactNode) => ReactNode
}

export function JoyfulWebAppProvider({
  app,
  children,
  clerkPublishableKey,
  queryClient,
  renderQueryProvider,
}: JoyfulWebAppProviderProps) {
  const clerkDomain = getClerkDomain()
  const appProvider = (
    <DocumentTitleProvider>
      <GlobalAgentStreamingProvider>
        <JoyfulAppProvider app={app}>
          {children}
          <AgentTabNotification />
          <Toaster />
        </JoyfulAppProvider>
      </GlobalAgentStreamingProvider>
    </DocumentTitleProvider>
  )
  const queryProvider = renderQueryProvider ? (
    renderQueryProvider(appProvider)
  ) : queryClient ? (
    <QueryClientProvider client={queryClient}>
      {appProvider}
    </QueryClientProvider>
  ) : (
    appProvider
  )
  const telemetryProvider = (
    <ErrorBoundary fallback={JoyfulWebAppErrorFallback}>
      {queryProvider}
    </ErrorBoundary>
  )

  return (
    <LazyMotion features={domMax} strict>
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        signInUrl="/sign-in"
        signInFallbackRedirectUrl="/"
        afterSignOutUrl="/"
        isSatellite={false}
        domain={clerkDomain}
      >
        <ThemeProvider>
          <TooltipProvider>
            <HelmetProvider>
              <NoOpRedactionProvider>{telemetryProvider}</NoOpRedactionProvider>
            </HelmetProvider>
          </TooltipProvider>
        </ThemeProvider>
      </ClerkProvider>
    </LazyMotion>
  )
}

function getClerkDomain() {
  if (globalThis.location.hostname.endsWith('.joyfulhealth.io')) {
    return 'joyfulhealth.io'
  }

  return 'joyfulhealth.com'
}

interface JoyfulWebAppErrorFallbackProps {
  error: Error
  resetError: () => void
}

function JoyfulWebAppErrorFallback(props: JoyfulWebAppErrorFallbackProps) {
  reportBrowserError({
    errorName: props.error.name,
    kind: 'react_error_boundary',
    message: props.error.message,
    stack: props.error.stack,
  })
  if (shouldSuppressStaleClientBundleError(props.error)) {
    return null
  }

  return (
    <div
      className="bg-tremor-background flex min-h-svh items-center justify-center p-6"
      role="alert"
    >
      <ErrorState error={props.error}>
        <Button onClick={props.resetError} variant="brand">
          {`Try again`}
        </Button>
      </ErrorState>
    </div>
  )
}
